import { useMemo } from "react";
import { encode } from "uqr";
import { useI18n } from "../../i18n";

/**
 * The batch's QR and its six digits (DA-10-21), drawn on the phone from the
 * payload — no network image, so it shows in a basement. The QR carries the
 * handover id, which the desk's scan field opens (EP03-S20); the digits are
 * for a desk that types instead. Black on white in either theme: a scanner
 * reads dark modules on a light ground.
 */
export function HandoverQr({ payload, code }: { payload: string; code: string }) {
	const { t } = useI18n();
	const qr = useMemo(() => encode(payload, { ecc: "M", border: 2 }), [payload]);
	const path = useMemo(
		() =>
			qr.data
				.flatMap((row, y) => row.map((dark, x) => (dark ? `M${x} ${y}h1v1h-1z` : "")))
				.join(""),
		[qr],
	);
	return (
		<figure className="flex flex-col items-center gap-2">
			<svg
				role="img"
				aria-label={t("driver.handover.qr")}
				viewBox={`0 0 ${qr.size} ${qr.size}`}
				className="w-full max-w-72 rounded-lg bg-white text-black"
				shapeRendering="crispEdges"
			>
				<path d={path} fill="currentColor" />
			</svg>
			<figcaption className="flex flex-col items-center gap-1">
				<span className="text-[13px] text-fg-muted">{t("driver.handover.code")}</span>
				<span dir="ltr" className="font-mono text-[32px] font-bold tracking-[0.3em] text-fg">
					{code}
				</span>
			</figcaption>
		</figure>
	);
}
